/**
 * Normalize editor-block provenance (Source shape) and optional firma attribution.
 */

export const RESOLVABLE_SOURCES = ['collaboratori', 'import_stories', 'archive'];

/** Upload placeholders that must not win over usage_source from a dragged pack. */
export const GENERIC_SOURCE_NAMES = ['url', 'local'];

/** Default byline when media comes from Midjourney / Sources.AI. */
export const AI_DEFAULT_AUTHOR = 'Midjourney';

/**
 * Aliases used by drag packs / KnownSources that map onto resolvable names.
 *
 * @type {Record<string, string>}
 */
export const RESOLVABLE_SOURCE_ALIASES = {
	milenium: 'import_stories',
	collaborators: 'collaboratori',
	archives: 'archive',
};

/**
 * @returns {{name: null, identifier: null, signature: null, url: null, file: null, keywords: null}}
 */
export function emptySource() {
	return {
		name: null,
		identifier: null,
		signature: null,
		url: null,
		file: null,
		keywords: null,
	};
}

/**
 * @param {unknown} raw
 * @returns {string[]|null}
 */
export function normalizeSourceKeywords(raw) {
	if (raw == null || raw === '') {
		return null;
	}

	let values = raw;

	if (typeof values === 'string') {
		values = values.split(',').map((part) => part.trim());
	}

	if (!Array.isArray(values)) {
		return null;
	}

	const keywords = [];
	const seen = new Set();

	for (const value of values) {
		if (value == null || value === '') {
			continue;
		}

		const normalized = String(value).trim();

		if (normalized === '' || normalized === 'null') {
			continue;
		}

		const key = normalized.toLowerCase();

		if (seen.has(key)) {
			continue;
		}

		seen.add(key);
		keywords.push(normalized);
	}

	return keywords.length > 0 ? keywords : null;
}

/**
 * @returns {{journalist_id: null, tags: number[]}}
 */
export function emptyAttribution() {
	return {
		journalist_id: null,
		tags: [],
	};
}

/**
 * Normalize attribution tag ids (family / row ids from the content tags Select2).
 *
 * @param {unknown} raw
 * @returns {number[]}
 */
export function normalizeAttributionTags(raw) {
	if (!Array.isArray(raw)) {
		return [];
	}

	const seen = new Set();
	const tags = [];

	for (const value of raw) {
		const id = Number(value);

		if (!Number.isFinite(id) || id <= 0 || seen.has(id)) {
			continue;
		}

		seen.add(id);
		tags.push(id);
	}

	return tags;
}

/**
 * @param {unknown} raw
 * @returns {{journalist_id: number|null, tags: number[]}}
 */
export function normalizeAttribution(raw) {
	const attribution = emptyAttribution();

	if (!raw || typeof raw !== 'object') {
		return attribution;
	}

	if (raw.journalist_id != null && raw.journalist_id !== '') {
		const parsed = Number(raw.journalist_id);
		attribution.journalist_id = Number.isFinite(parsed) ? parsed : null;
	}

	attribution.tags = normalizeAttributionTags(raw.tags);

	return attribution;
}

/**
 * @param {string|null|undefined} name
 * @returns {string}
 */
export function canonicalizeSourceName(name) {
	const normalized = String(name || '').trim().toLowerCase();

	if (!normalized) {
		return '';
	}

	return RESOLVABLE_SOURCE_ALIASES[normalized] || normalized;
}

/**
 * @param {string|null|undefined} name
 * @returns {boolean}
 */
export function isGenericSourceName(name) {
	return GENERIC_SOURCE_NAMES.includes(canonicalizeSourceName(name));
}

/**
 * @param {string|null|undefined} name
 * @returns {boolean}
 */
export function isAiSource(name) {
	return canonicalizeSourceName(name) === 'ai';
}

/**
 * Midjourney / AI byline: keep IPTC (or existing) author when present, else "Midjourney".
 * Call after IPTC extraction (or when IPTC fill is disabled and author is empty).
 *
 * @param {unknown} existingAuthor
 * @returns {string}
 */
export function resolveAiAuthor(existingAuthor) {
	return nullableString(existingAuthor) || AI_DEFAULT_AUTHOR;
}

/**
 * @param {string|null|undefined} name
 * @returns {boolean}
 */
export function isResolvableSource(name) {
	return RESOLVABLE_SOURCES.includes(canonicalizeSourceName(name));
}

/**
 * @param {unknown} value
 * @returns {string|null}
 */
function nullableString(value) {
	if (value === null || value === undefined || value === '' || value === 'null') {
		return null;
	}

	if (typeof value !== 'string' && typeof value !== 'number') {
		return null;
	}

	const normalized = String(value).trim();

	return normalized === '' ? null : normalized;
}

/**
 * Flatten import-story / offcanvas signature payloads into a single Source.signature string.
 *
 * @param {unknown} value
 * @returns {string|null}
 */
export function flattenSourceSignature(value) {
	if (Array.isArray(value)) {
		const parts = value.map((item) => nullableString(item)).filter(Boolean);

		return parts.length ? parts.join(', ') : null;
	}

	return nullableString(value);
}

/**
 * @param {object|string|null|undefined} raw
 * @param {{source_url?: unknown, usage_source?: unknown, usage_identifier?: unknown, signature?: unknown, source_name?: unknown, keywords?: unknown}} [extras]
 * @returns {{name: string|null, identifier: string|null, signature: string|null, url: string|null, file: string|null, keywords: string[]|null}}
 */
export function normalizeSource(raw, extras = {}) {
	const source = emptySource();

	if (typeof raw === 'string') {
		source.name = nullableString(raw);
	} else if (raw && typeof raw === 'object') {
		source.name = nullableString(raw.name);
		source.identifier = nullableString(raw.identifier);
		source.signature = flattenSourceSignature(raw.signature);
		source.url = nullableString(raw.url);
		source.file = nullableString(raw.file);
		source.keywords = normalizeSourceKeywords(raw.keywords);
	}

	const usageName = nullableString(extras.usage_source) || nullableString(extras.source_name);

	// Dragged media uploads stamp Sources.URL on the file; prefer the pack usage label.
	if (!source.name || (usageName && isGenericSourceName(source.name))) {
		source.name = usageName || source.name;
	}

	if (source.name) {
		const canonical = canonicalizeSourceName(source.name);
		source.name = canonical || source.name;
	}

	if (!source.url) {
		source.url = nullableString(extras.source_url);
	}

	if (!source.identifier) {
		source.identifier = nullableString(extras.usage_identifier);
	}

	if (!source.signature) {
		source.signature = flattenSourceSignature(extras.signature);
	}

	if (!source.keywords) {
		source.keywords = normalizeSourceKeywords(extras.keywords);
	}

	return source;
}

/**
 * @param {{name?: string|null, identifier?: string|null, signature?: string|null, url?: string|null, file?: string|null}|null|undefined} source
 * @returns {{source: object, source_name: string, source_url: string}}
 */
export function dualWriteSourceFields(source) {
	const normalized = normalizeSource(source);

	return {
		source: normalized,
		source_name: normalized.name || '',
		source_url: normalized.url || '',
	};
}

/**
 * @param {object|null|undefined} data
 * @returns {{source: object, attribution: {journalist_id: number|null, tags: number[]}, author: string, source_name: string, source_url: string}}
 */
export function normalizeBlockMeta(data = {}) {
	const payload = data && typeof data === 'object' ? data : {};
	const source = normalizeSource(payload.source, {
		source_url: payload.source_url,
		source_name: payload.source_name,
		usage_source: payload.usage_source,
		usage_identifier: payload.usage_identifier,
		signature: payload.source?.signature ?? payload.signature,
	});

	const dual = dualWriteSourceFields(source);

	return {
		source: dual.source,
		attribution: normalizeAttribution(payload.attribution),
		author: payload.author == null ? '' : String(payload.author),
		source_name: dual.source_name,
		source_url: dual.source_url,
	};
}

/**
 * @param {string} author
 * @param {string} firmaName
 * @returns {string}
 */
export function applyOneShotAuthor(author, firmaName) {
	return String(author || '').trim() === '' ? String(firmaName || '') : author;
}

/**
 * @param {{author?: string, attribution?: {journalist_id?: number|null, tags?: number[]}}} meta
 * @param {{id?: number|string, name?: string}|null} result
 * @returns {{author: string, attribution: {journalist_id: number|null, tags: number[]}}}
 */
export function applyUniqueResolveResult(meta, result) {
	const previous = normalizeAttribution(meta?.attribution);

	if (!result || result.id == null) {
		return {
			author: meta.author || '',
			attribution: previous,
		};
	}

	return {
		author: applyOneShotAuthor(meta.author || '', result.name || ''),
		attribution: {
			journalist_id: Number(result.id),
			tags: previous.tags,
		},
	};
}

/**
 * @param {string} sourceName
 * @param {string} q
 * @returns {Promise<Array<{id: number, name: string}>>}
 */
export async function resolveAttribution(sourceName, q) {
	const canonicalSource = canonicalizeSourceName(sourceName);

	if (!isResolvableSource(canonicalSource)) {
		return [];
	}

	const query = String(q || '').trim();

	if (!query) {
		return [];
	}

	const base = typeof window !== 'undefined' ? window.appconfig?.routes?.attribution?.resolve : null;

	if (!base) {
		return [];
	}

	try {
		const url = `${base}?${new URLSearchParams({ source: canonicalSource, q: query })}`;
		const response = await fetch(url, {
			headers: { Accept: 'application/json' },
			credentials: 'same-origin',
		});

		if (!response.ok) {
			return [];
		}

		const json = await response.json();

		return Array.isArray(json?.results) ? json.results : [];
	} catch {
		return [];
	}
}

/**
 * @param {object|null|undefined} blockData
 * @returns {Promise<object>}
 */
export async function maybeResolveBlockAttribution(blockData) {
	const meta = normalizeBlockMeta(blockData || {});
	const sourceName = canonicalizeSourceName(meta.source?.name);

	if (!isResolvableSource(sourceName)) {
		return {
			...meta,
			attribution: meta.attribution || emptyAttribution(),
		};
	}

	const q = String(meta.source?.signature || meta.author || '').trim();
	const results = await resolveAttribution(sourceName, q);

	if (results.length !== 1) {
		return {
			...meta,
			attribution: {
				journalist_id: null,
				tags: meta.attribution.tags,
			},
			...dualWriteSourceFields(meta.source),
		};
	}

	const applied = applyUniqueResolveResult(
		{ author: meta.author, attribution: meta.attribution },
		results[0],
	);

	return {
		...meta,
		...applied,
		...dualWriteSourceFields(meta.source),
	};
}
